<template>
  <div class="admin-page">
    <div class="admin-header">
      <div>
        <router-link to="/pointe/admin/applications" class="back-link">← Applications</router-link>
        <h1>Review Application</h1>
      </div>
      <div class="header-chips">
        <span v-if="application" class="status-badge" :class="application.status">{{ statusLabel(application.status) }}</span>
        <span v-if="application && application.isHistorical" class="badge-historical">Historical</span>
      </div>
    </div>

    <div v-if="loading" class="loading-wrap">
      <v-progress-circular indeterminate color="#1a1a1a"></v-progress-circular>
    </div>

    <div v-else-if="!application" class="empty-state">
      <p>Application not found.</p>
    </div>

    <div v-else class="review-layout">
      <!-- Left: Application Info -->
      <div class="info-panel">
        <div class="info-card">
          <h3>Dancer</h3>
          <p class="info-val">{{ application.dancerName }}</p>
          <p class="info-email">{{ application.dancerEmail }}</p>
        </div>

        <div class="info-card">
          <h3>Cohort</h3>
          <p class="info-val">{{ application.cohortName }}</p>
        </div>

        <div class="info-card">
          <h3>Payment</h3>
          <p class="info-val">
            <span class="payment-badge" :class="application.paymentStatus">{{ application.paymentStatus }}</span>
          </p>
          <p v-if="application.memberDiscountApplied" class="info-note">Member discount applied (25%)</p>
          <button
            v-if="application.paymentStatus !== 'refunded'"
            class="btn-refund"
            @click="markRefunded"
          >Mark Refunded</button>
        </div>

        <div class="info-card">
          <h3>Video Upload</h3>
          <div v-if="application.isHistorical">
            <p class="info-note">Historical record</p>
            <a v-if="application.pdfUrl" :href="application.pdfUrl" target="_blank" class="link-doc">View PDF →</a>
          </div>
          <div v-else>
            <p v-if="application.videoConfirmed" class="info-val confirmed">✓ Dancer confirmed upload</p>
            <p v-else class="info-note">Not yet confirmed</p>
          </div>
        </div>

        <!-- Questionnaire -->
        <div v-if="application.questionnaire" class="info-card questionnaire-card">
          <h3>Application Questionnaire</h3>
          <div v-for="(val, key) in application.questionnaire" :key="key" class="q-row">
            <span class="q-key">{{ formatKey(key) }}</span>
            <span class="q-val">{{ val }}</span>
          </div>
        </div>

        <div v-if="application.historyQuestions" class="info-card questionnaire-card">
          <h3>Foot 360 History</h3>
          <div v-for="(val, key) in application.historyQuestions" :key="key" class="q-row">
            <span class="q-key">{{ formatKey(key) }}</span>
            <span class="q-val">{{ Array.isArray(val) ? val.join(', ') : val }}</span>
          </div>
        </div>

        <!-- Link Consultation -->
        <div class="info-card">
          <h3>Linked Consultation</h3>
          <p v-if="application.linkedConsultationId" class="info-val">
            <router-link :to="'/pointe/admin/consultations'" class="link-doc">Consultation linked</router-link>
          </p>
          <div v-else>
            <input v-model="consultationSearch" type="text" class="form-input" placeholder="Search by dancer email…" @input="searchConsultations" />
            <div v-if="consultationResults.length" class="consult-results">
              <div
                v-for="c in consultationResults"
                :key="c.id"
                class="consult-result-row"
                @click="linkConsultation(c)"
              >
                {{ c.dancerEmail }} — {{ formatDate(c.consultationDate) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Review Panel -->
      <div class="review-panel">
        <div class="criteria-section">
          <h2>Criteria Review</h2>
          <p class="section-hint">Check each criterion and add notes as needed.</p>

          <div v-if="criteria.length === 0" class="empty-state">
            <p>No active criteria. <router-link to="/pointe/admin/criteria">Add criteria →</router-link></p>
          </div>

          <div v-for="criterion in criteria" :key="criterion.id" class="criterion-item">
            <div class="criterion-header">
              <div class="criterion-toggle">
                <button
                  class="toggle-btn"
                  :class="{ pass: getCriterionResult(criterion.id) === true, fail: getCriterionResult(criterion.id) === false }"
                  @click="cycleCriterion(criterion.id)"
                >
                  <span v-if="getCriterionResult(criterion.id) === true">✓ Pass</span>
                  <span v-else-if="getCriterionResult(criterion.id) === false">✗ Needs Work</span>
                  <span v-else>— Not Reviewed</span>
                </button>
              </div>
              <div class="criterion-name">{{ criterion.title }}</div>
              <div v-if="criterion.category" class="criterion-cat">{{ criterion.category }}</div>
            </div>
            <textarea
              v-model="criteriaNotes[criterion.id]"
              class="notes-input"
              :placeholder="'Notes for ' + criterion.title + ' (optional)'"
              rows="2"
            ></textarea>
          </div>
        </div>

        <div class="overall-section">
          <h2>Overall Result</h2>
          <div class="result-buttons">
            <button
              class="result-btn approved"
              :class="{ selected: overallResult === 'approved' }"
              @click="overallResult = 'approved'"
            >Approved</button>
            <button
              class="result-btn needs-work"
              :class="{ selected: overallResult === 'needs_work' }"
              @click="overallResult = 'needs_work'"
            >Needs Work</button>
          </div>
        </div>

        <div class="status-section">
          <h2>Application Status</h2>
          <select v-model="newStatus" class="form-input">
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="needs_work">Needs Work</option>
          </select>
        </div>

        <div class="notes-section">
          <h2>Admin Notes</h2>
          <textarea v-model="adminNotes" class="notes-input" rows="4" placeholder="Internal notes (not shown to dancer)…"></textarea>
        </div>

        <p v-if="saveSuccess" class="success-text">Saved and email sent.</p>
        <p v-if="saveError" class="error-text">{{ saveError }}</p>

        <div class="save-actions">
          <button class="btn-primary" :disabled="saving" @click="saveAndNotify">
            {{ saving ? 'Saving…' : 'Save & Notify Dancer' }}
          </button>
          <button class="btn-secondary" :disabled="saving" @click="saveOnly">
            Save Only
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db, functions } from '@/firebase'

export default {
  props: {
    id: { type: String, required: true }
  },
  data () {
    return {
      application: null,
      criteria: [],
      loading: true,
      saving: false,
      saveSuccess: false,
      saveError: '',
      criteriaResults: {},
      criteriaNotes: {},
      overallResult: null,
      newStatus: 'under_review',
      adminNotes: '',
      consultationSearch: '',
      consultationResults: []
    }
  },
  async created () {
    const [appDoc, criteriaSnap] = await Promise.all([
      db.collection('pointe-applications').doc(this.id).get(),
      db.collection('pointe-criteria').where('active', '==', true).orderBy('order').get()
    ])
    if (appDoc.exists) {
      this.application = { id: appDoc.id, ...appDoc.data() }
      this.newStatus = this.application.status
      this.overallResult = this.application.overallResult || null
      this.adminNotes = this.application.adminNotes || ''
      // Populate existing criteria results
      const existingCriteria = this.application.criteria || {}
      for (const [cid, val] of Object.entries(existingCriteria)) {
        this.$set(this.criteriaResults, cid, val.passed)
        this.$set(this.criteriaNotes, cid, val.adminNotes || '')
      }
    }
    this.criteria = criteriaSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    // Initialize notes for any criteria not yet in application
    this.criteria.forEach(c => {
      if (!(c.id in this.criteriaNotes)) {
        this.$set(this.criteriaNotes, c.id, '')
      }
    })
    this.loading = false
  },
  methods: {
    getCriterionResult (id) {
      return this.criteriaResults[id] !== undefined ? this.criteriaResults[id] : null
    },
    cycleCriterion (id) {
      const current = this.criteriaResults[id]
      if (current === undefined || current === null) {
        this.$set(this.criteriaResults, id, true)
      } else if (current === true) {
        this.$set(this.criteriaResults, id, false)
      } else {
        this.$set(this.criteriaResults, id, null)
      }
    },
    buildCriteriaObj () {
      const obj = {}
      this.criteria.forEach(c => {
        obj[c.id] = {
          passed: this.criteriaResults[c.id] !== undefined ? this.criteriaResults[c.id] : null,
          adminNotes: this.criteriaNotes[c.id] || ''
        }
      })
      return obj
    },
    async saveOnly () {
      await this.doSave(false)
    },
    async saveAndNotify () {
      await this.doSave(true)
    },
    async doSave (notify) {
      this.saving = true
      this.saveError = ''
      this.saveSuccess = false
      try {
        const update = {
          criteria: this.buildCriteriaObj(),
          status: this.newStatus,
          overallResult: this.overallResult,
          adminNotes: this.adminNotes,
          reviewedAt: new Date()
        }
        await db.collection('pointe-applications').doc(this.id).update(update)
        this.application = { ...this.application, ...update }
        if (notify) {
          const sendEmail = functions.httpsCallable('sendPointeStatusEmail')
          await sendEmail({ applicationId: this.id, newStatus: this.newStatus })
          this.saveSuccess = true
        }
      } catch (e) {
        this.saveError = e.message
      }
      this.saving = false
    },
    async markRefunded () {
      if (!confirm('Mark this application as refunded? This does not call Stripe.')) return
      await db.collection('pointe-applications').doc(this.id).update({
        paymentStatus: 'refunded',
        status: 'refunded'
      })
      this.application.paymentStatus = 'refunded'
      this.application.status = 'refunded'
    },
    async searchConsultations () {
      if (!this.consultationSearch || this.consultationSearch.length < 3) {
        this.consultationResults = []
        return
      }
      const q = this.consultationSearch.toLowerCase()
      const snap = await db.collection('pointe-consultations')
        .where('dancerEmail', '==', q)
        .limit(5)
        .get()
      this.consultationResults = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    },
    async linkConsultation (c) {
      await db.collection('pointe-applications').doc(this.id).update({ linkedConsultationId: c.id })
      this.application.linkedConsultationId = c.id
      this.consultationResults = []
      this.consultationSearch = ''
    },
    statusLabel (status) {
      const map = {
        submitted: 'Submitted', under_review: 'Under Review',
        approved: 'Approved', needs_work: 'Needs Work',
        awaiting_video: 'Awaiting Video', pending_payment: 'Pending Payment',
        draft: 'Draft', refunded: 'Refunded'
      }
      return map[status] || status
    },
    formatDate (ts) {
      if (!ts) return '—'
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    formatKey (key) {
      return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
    }
  }
}
</script>

<style scoped>
.admin-page {
  max-width: 1300px;
  margin: 0 auto;
  padding: 32px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
}

.admin-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
}

.admin-header h1 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 26px;
  color: #1a1a1a;
  margin: 4px 0 0;
}

.back-link { font-size: 13px; color: #888; text-decoration: none; }
.back-link:hover { color: #1a1a1a; }

.header-chips { display: flex; gap: 8px; align-items: center; margin-top: 6px; }

.badge-historical {
  background: #e8eaf6;
  color: #283593;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.loading-wrap { padding: 60px; text-align: center; }
.empty-state { padding: 40px; text-align: center; color: #888; }

.review-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 32px;
  align-items: start;
}

.info-panel { display: flex; flex-direction: column; gap: 16px; }

.info-card {
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 16px;
  background: #fff;
}

.info-card h3 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #888;
  margin: 0 0 8px;
}

.info-val { font-size: 16px; font-weight: 600; color: #1a1a1a; margin: 0 0 4px; }
.info-email { font-size: 13px; color: #888; margin: 0; }
.info-note { font-size: 13px; color: #888; margin: 4px 0; font-style: italic; }
.confirmed { color: #2e7d32; }

.q-row { display: flex; gap: 12px; padding: 6px 0; border-bottom: 1px solid #f0eeec; font-size: 13px; }
.q-row:last-child { border-bottom: none; }
.q-key { min-width: 120px; color: #888; flex-shrink: 0; }
.q-val { color: #1a1a1a; }

.questionnaire-card { max-height: 200px; overflow-y: auto; }

.link-doc { font-size: 13px; color: #1a1a1a; }

.consult-results {
  border: 1px solid #e8e5e1;
  border-radius: 2px;
  margin-top: 4px;
  background: #fff;
}

.consult-result-row {
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  border-bottom: 1px solid #f0eeec;
}

.consult-result-row:last-child { border-bottom: none; }
.consult-result-row:hover { background: #f9f8f6; }

/* Review panel */
.review-panel { display: flex; flex-direction: column; gap: 24px; }

.review-panel h2 {
  font-family: 'Quattrocento', Georgia, serif;
  font-size: 18px;
  color: #1a1a1a;
  margin: 0 0 12px;
}

.section-hint { font-size: 13px; color: #888; margin: -8px 0 12px; }

.criterion-item {
  border: 1px solid #e8e5e1;
  border-radius: 4px;
  padding: 14px 16px;
  background: #fff;
  margin-bottom: 10px;
}

.criterion-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.criterion-name { font-size: 15px; font-weight: 600; color: #1a1a1a; flex: 1; }
.criterion-cat { font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: .05em; }

.toggle-btn {
  border: none;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-family: 'Fanwood Text', Georgia, serif;
  background: #f0f0f0;
  color: #888;
  white-space: nowrap;
  transition: background 0.15s;
}

.toggle-btn.pass { background: #e8f5e9; color: #2e7d32; }
.toggle-btn.fail { background: #fce4ec; color: #c62828; }

.notes-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 8px 10px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 14px;
  border-radius: 2px;
  box-sizing: border-box;
  resize: vertical;
  background: #faf9f7;
  color: #1a1a1a;
}

.notes-input:focus { outline: none; border-color: #1a1a1a; background: #fff; }

.result-buttons { display: flex; gap: 12px; }

.result-btn {
  flex: 1;
  padding: 14px;
  border: 2px solid #e8e5e1;
  border-radius: 4px;
  background: #fff;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.15s;
}

.result-btn.approved.selected { border-color: #2e7d32; background: #e8f5e9; color: #2e7d32; }
.result-btn.needs-work.selected { border-color: #c62828; background: #fce4ec; color: #c62828; }

.form-input {
  width: 100%;
  border: 1px solid #e8e5e1;
  padding: 10px 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  border-radius: 2px;
  box-sizing: border-box;
  color: #1a1a1a;
  background: #fff;
}

.form-input:focus { outline: none; border-color: #1a1a1a; }

.save-actions { display: flex; gap: 12px; }

.btn-primary {
  background: #1a1a1a;
  color: #fff;
  border: none;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
}

.btn-primary:hover { background: #333; }
.btn-primary:disabled { opacity: 0.5; cursor: default; }

.btn-secondary {
  background: #fff;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  padding: 12px 24px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 15px;
  cursor: pointer;
  border-radius: 2px;
}

.btn-refund {
  background: none;
  border: 1px solid #c00;
  color: #c00;
  padding: 6px 12px;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 13px;
  cursor: pointer;
  border-radius: 2px;
  margin-top: 8px;
}

.btn-refund:hover { background: #fce4ec; }

.success-text { color: #2e7d32; font-size: 14px; }
.error-text { color: #c00; font-size: 14px; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.status-badge.submitted { background: #e3f2fd; color: #1565c0; }
.status-badge.under_review { background: #fff8e1; color: #f57f17; }
.status-badge.approved { background: #e8f5e9; color: #2e7d32; }
.status-badge.needs_work { background: #fce4ec; color: #c62828; }
.status-badge.awaiting_video { background: #f3e5f5; color: #6a1b9a; }
.status-badge.pending_payment { background: #fff3e0; color: #e65100; }
.status-badge.draft { background: #f5f5f5; color: #616161; }
.status-badge.refunded { background: #f5f5f5; color: #9e9e9e; }

.payment-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.payment-badge.paid { background: #e8f5e9; color: #2e7d32; }
.payment-badge.unpaid { background: #fff3e0; color: #e65100; }
.payment-badge.free_follow_up { background: #e8eaf6; color: #283593; }
.payment-badge.refunded { background: #f5f5f5; color: #9e9e9e; }

@media (max-width: 900px) {
  .review-layout { grid-template-columns: 1fr; }
}
</style>
